import React, { useContext } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Stack, Text } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Routes } from '~/domains/common/constants/routes';
import { SegmentedControl } from '~/domains/common/components/SegmentedControl/SegmentedControl';
import { AppDownloadPrompt } from '~/domains/common/components/AppDownloadPrompt';
import { ConnectedSignUpModal } from '~/domains/common/components/SignUpModal/ConnectedSignUpModal';
import Posts from '~/domains/commissioner/components/Posts/Posts';
import dt from '~/testing';
import Modal from '~/domains/common/components/Modal/Modal/Modal';
import { getHandle } from '~/domains/user/utils/getHandle';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import { useContestSlates } from '~/domains/contest/hooks/useContestSlates';
import { ContestChatModalContext } from '~/domains/contest/domains/gamecenter/domains/common/components/ContestChat/ContestChatModals/ContestChatModalContext';
import PromoCarousel from '~/domains/promotions/components/PromoCarousel/PromoCarousel';
import { PromoCarouselLocation } from '~/domains/promotions';

import { Section, SectionContent, SectionTitle } from '../Sections/components';
import { ContestHeader } from '../ContestHeader/ContestHeader';
import Entrants from '../Entrants/Entrants';
import MyEntries from '../Sections/MyEntries/MyEntries';
import AboutSection from '../Sections/About/About';
import PrizesSection from '../Sections/Prizes/Prizes';
import RulesSection from '../Sections/Rules';
import { ScoringAndRulesSection } from '../Sections/ScoringAndRulesSection';
import ContestSettingsSection from '../Sections/ContestSettings/ContestSettings';
import ScheduleSection from '../Sections/Schedule/Schedule';
import LongRules from '../Sections/LongRules';
import AdditionalPayoutCountdownWidget from '../../../common/components/AdditionalPayoutCountdownWidget/AdditionalPayoutCountdownWidget';
import { PromoCallouts } from '../PromoCallouts/PromoCallouts';

import useStyles from './useStyles';
import useContestDetail, { ContentTab } from './useContestDetail';

type ContestDetailProps = {
  isSidePanel?: boolean;
  hideEntries?: boolean;
  hideCTA?: boolean;
};

function ContestDetail({ isSidePanel, hideEntries, hideCTA }: ContestDetailProps) {
  // Get chat panel state to adjust layout when panel is open
  const { isChatPanelOpen, isChatPanelClosing, chatOffset } = useContext(ContestChatModalContext);
  const isTablet = useIsTablet();

  // When chat panel is open on desktop, we want the content to be full width
  const isChatOpenForStyles = isChatPanelOpen && !isChatPanelClosing && !isTablet;

  const { classes, theme } = useStyles({
    isSidePanel,
    isChatOpen: isChatOpenForStyles,
    chatOffset,
  });
  const { t } = useTranslation('contest');
  const {
    activeTab,
    channel,
    contestData,
    handleDownloadClick,
    isAllowedInLocation,
    isAppPromptModalShown,
    isNewEntriesAllowed,
    isMobile,
    isTabsShown,
    maxEntriesPerUser,
    myEntriesStatus,
    openEntryNameEditModal,
    setActiveTab,
    setShowAppPromptModal,
    tabs,
    user,
    userEntries,
  } = useContestDetail({ isSidePanel, hideEntries });
  const isEntertainmentOnly = contestData.settings?.entertainmentOnlySettings?.enabled;

  const { data: contestSlates } = useContestSlates(contestData?.id);

  const slatesForDisplay = contestData.slates;

  /** Temporary until we get a better fix in */
  const newContestSlates = contestSlates;

  const showFormattedRules = contestData.formattedRules && contestData.formattedRules.length > 0;
  return (
    <>
      {isAppPromptModalShown && (
        <Modal
          onClose={() => setShowAppPromptModal(false)}
          isOpened={isAppPromptModalShown}
          withCloseButton={false}
          closeOnClickOutside={false}
          size="xl"
        >
          <AppDownloadPrompt
            title={t('detail.appPromo.title')}
            subtitle={t('detail.appPromo.subtitle')}
            onDownloadClick={() => handleDownloadClick()}
            onContinueClick={() => {
              setShowAppPromptModal(false);
            }}
            isMobile={isMobile}
            contest={contestData}
          />
        </Modal>
      )}
      <ContestHeader
        contestId={contestData.id}
        isAllowedInLocation={isAllowedInLocation}
        isSidePanel={isSidePanel}
        hideCTA={hideCTA}
      />
      {isNewEntriesAllowed && (<div className={classes.promoContainer}>
        <div className={classes.promoContent}>
          <PromoCarousel
            location={PromoCarouselLocation.CONTEST}
            contestId={contestData.id}
            backgroundColor={theme.colors.gray2[0]}
          />
        </div>
      </div>)}
      <section className={classes.contentContainer}>
        <div className={classes.content}>
          <div className={classes.main}>
            {user && isTabsShown ? (
              <SegmentedControl
                value={activeTab}
                onChange={(v) => {
                  setActiveTab(v as ContentTab);
                }}
                data={tabs.map(({ label, value }) => ({
                  value,
                  label,
                }))}
                w="100%"
                mb="lg"
              />
            ) : null}
            <Stack spacing="xl">
              {!isTabsShown || activeTab === ContentTab.DETAILS ? (
                <>
                  <PromoCallouts contestData={contestData} />
                  {contestData?.description ? (
                    <SectionContent>
                      <Text
                        data-test-id={dt.contestDetail.page.decription}
                        m={0}
                        component="p"
                        className={classes.description}
                      >
                        {contestData.description}
                      </Text>
                    </SectionContent>
                  ) : null}
                  {user && userEntries > 0 && !isSidePanel && !hideEntries ? (
                    <MyEntries
                      contestId={contestData.id}
                      myEntriesStatus={myEntriesStatus}
                      openEntryNameEditModal={openEntryNameEditModal}
                      fullContest={contestData}
                    />
                  ) : null}
                  {!isEntertainmentOnly && (
                    <>
                      <AdditionalPayoutCountdownWidget
                        contest={contestData}
                        isSidePanel={isSidePanel}
                      />
                      <PrizesSection
                        prizes={contestData.payout_schedule}
                        segments={contestData.settings?.segments}
                        isSidePanel={isSidePanel}
                        resizingMode={contestData.resizing_mode}
                        contestType={contestData.contest_type}
                        slateLabel={contestData.settings?.slateLabel}
                        contestTags={contestData.tags}
                        buybackPrizePoolInDollars={contestData.buybackPrizePoolInDollars}
                        buybackSettings={contestData.settings?.buybacks}
                        contest={contestData}
                      />
                    </>
                  )}
                  <AboutSection
                    description={contestData.rich_short_rules || contestData.short_rules}
                    rosterRequirements={contestData.roster_requirements}
                    maxEntriesPerUser={maxEntriesPerUser}
                    contestType={contestData.contest_type}
                    createdByAdmin={contestData.created_by_admin}
                    owner={getHandle(contestData.commissioner)}
                    ownerHref={Routes.channel(contestData.commissioner.handle)}
                    sport={contestData.league.sport}
                    leagueName={contestData.league.name}
                    isSidePanel={isSidePanel}
                    pickDeadlineType={contestData.settings?.pickDeadlineType}
                    entryDeadline={contestData.start_date}
                    slatesInfo={contestData.slatesInfo}
                    scheduleId={contestData.schedule_id}
                    finalSlateId={contestData?.final_slate_id}
                    slateLabel={contestData.settings?.slateLabel}
                    contestSlates={slatesForDisplay}
                    newContestSlates={newContestSlates}
                    segments={contestData.settings?.segments}
                    isEntryDeadlinePending={contestData?.is_entry_deadline_pending}
                    groupStageSlateId={contestData.settings?.groupStageSettings?.slateId}
                  />
                  <ScheduleSection
                    slates={slatesForDisplay}
                    segments={contestData.settings?.segments}
                    isSidePanel={isSidePanel}
                    slateLabel={contestData.settings?.slateLabel}
                    isEntryDeadlinePending={contestData?.is_entry_deadline_pending}
                    groupStageSlateId={contestData.settings?.groupStageSettings?.slateId}
                  />
                  <ContestSettingsSection
                    contestData={contestData}
                    contestSlates={slatesForDisplay}
                    isSidePanel={isSidePanel}
                  />
                  {showFormattedRules && (
                    <ScoringAndRulesSection
                      rules={contestData.formattedRules}
                      isSidePanel={isSidePanel}
                    />
                  )}
                  {!showFormattedRules && contestData.rules && (
                    <RulesSection rules={contestData.rules} isSidePanel={isSidePanel} />
                  )}
                  <LongRules longRules={contestData.long_rules} isSidePanel={isSidePanel} />
                </>
              ) : null}
              {user && isTabsShown && activeTab === ContentTab.ENTRANTS ? (
                <Section>
                  <SectionTitle isSidePanel={isSidePanel}>
                    {t('detail.entrants.title')}
                  </SectionTitle>
                  <SectionContent isSidePanel={isSidePanel}>
                    <Entrants contestId={contestData.id} />
                  </SectionContent>
                </Section>
              ) : null}
              {user && isTabsShown && activeTab === ContentTab.POSTS ? (
                <Section>
                  <Posts channel={channel} contestId={contestData.id} showCta smallText />
                </Section>
              ) : null}
            </Stack>
          </div>
          <aside className={classes.side}>
            {user && (
              <Posts
                channel={channel}
                contestId={contestData.id}
                showCta
                isInfiniteScroll={false}
              />
            )}
            {!hideEntries && user && (
              <>
                <SectionTitle>{t('detail.entrants.title')}</SectionTitle>
                <Entrants contestId={contestData.id} />
              </>
            )}
          </aside>
        </div>
      </section>
      <ConnectedSignUpModal />
    </>
  );
}

export default ContestDetail;
